import get from 'lodash/get'
import * as Yup from 'yup'
import locale from 'yup/lib/locale'

import {
  isLoginPhoneValid,
  isPhoneNumber,
  isPhoneValid
} from '@/utils/validation'

const invalidMessage = 'Invalid'

const evalFunc = (func, fallback = invalidMessage) =>
  (typeof func === 'function' ? func() : func) ?? fallback

Yup.addMethod(Yup.string, 'phone', function testPhone() {
  const phone = evalFunc(locale?.string?.phone)
  return this.test({
    name: 'phone',
    exclusive: true,
    params: { phone },
    // eslint-disable-next-line
    message: '${phone}', // Yup supports template strings like this
    test: isPhoneValid
  })
})

Yup.addMethod(Yup.string, 'phoneLogin', function testPhoneLogin(message) {
  const phone = evalFunc(locale?.string?.phone)
  return this.test({
    name: 'phone',
    exclusive: true,
    params: { phone },
    // eslint-disable-next-line
    message: message || '${phone}', // Yup supports template strings like this
    test: isLoginPhoneValid
  })
})

Yup.addMethod(Yup.string, 'phoneEmail', function testPhoneEmail() {
  return Yup.lazy((value = '') => {
    const isNumber = isPhoneNumber(value)
    const message = locale.string.phoneEmail
    return isNumber && value
      ? Yup.string().phoneLogin(message)
      : Yup.string().email(message).required(message)
  })
})

export default Yup

const FORMS = {
  en: {
    mixed: {
      default: invalidMessage,
      required: 'Required',
      notType: ({ type }) =>
        ({
          number: 'Please enter a valid number',
          date: 'Please enter a valid date'
        }[type])
    },
    number: {
      min: ({ min }) => `Should be at least ${min}`,
      max: ({ max }) => `Should be at most ${max}`,
      positive: 'Please enter a positive number',
      integer: 'Please enter an integer'
    },
    string: {
      min: ({ min }) => `Should be at least ${min} characters`,
      max: ({ max }) => `Should be at most ${max} characters`,
      length: ({ length }) => `Should be ${length} characters`,
      email: 'Please enter a valid email',
      phone: 'Please enter a valid phone number',
      phoneEmail: 'Please enter a valid email or phone number'
    },
    array: {
      min: 'Please, add at least one item'
    }
  },
  fr: {
    mixed: {
      default: 'Invalide',
      required: 'Obligatoire',
      notType: ({ type }) =>
        ({
          number: 'Entrez un nombre valide',
          date: 'Entrez une date valide'
        }[type])
    },
    number: {
      min: ({ min }) => `Doit être au moins ${min}`,
      max: ({ max }) => `Doit être au plus ${max}`,
      positive: 'Entrez un nombre positif',
      integer: 'Veuillez saisir un entier'
    },
    string: {
      min: ({ min }) => `Doit être au moins ${min} caractères`,
      max: ({ max }) => `Doit être au plus ${max} caractères`,
      length: ({ length }) => `Doit contenir ${length} caractères`,
      email: 'Entrer un email valide',
      phone: 'Entrez un numéro de téléphone valide',
      phoneEmail: 'Entrez un e-mail ou un numéro de téléphone valide'
    },
    array: {
      min: 'Veuillez ajouter au moins un élément'
    }
  },
  zh: {
    mixed: {
      default: '无效',
      required: '必填',
      notType: ({ type }) =>
        ({
          number: '请输入合格数字',
          date: '请出入有效日期'
        }[type])
    },
    number: {
      min: ({ min }) => `必须多余 ${min}`,
      max: ({ max }) => `必须少于 ${max}`,
      positive: '请输入正数',
      integer: '请输入整数'
    },
    string: {
      min: ({ min }) => `必须多余 ${min}`,
      max: ({ max }) => `必须少于 ${max}`,
      length: ({ length }) => `应为 ${length} 个字符`,
      email: '请输入有效电子邮件',
      phone: '请输入有效的手机号码',
      phoneEmail: '请输入有效邮箱或手机号码'
    },
    array: {
      min: '请添加至少一个项目'
    }
  }
}

/*
 * Gets the locale from Next.
 * The source of truth for this is NEXT_LOCALE
 * which is set from the header the browsers sends based on OS setting.
 * If a path pram is present, than that overrides the cookie.
 * However NEXT_LOCALE sets the path param if needed (not 'en'),
 * Therefore the URL will always contain a lang if not 'en'.
 */
const getLocale = () => {
  var lang
  if (typeof window !== 'undefined')
    lang = window.location.pathname.split('/')[1]
  if (!lang || ['en', 'fr', 'zh'].indexOf(lang) < 0) lang = 'en'
  return lang
}

/*
 * Creates a Yup locale schema which uses the validations messages defined
 * in the FORMS constant.  This is done by using a function
 * that is executed on validation that changes the message
 * depending on the current locale.
 *
 * By doing it this way, we don't need to import Yup to
 * our main app bundle to setup the translations,
 * since the translations don't get loaded now until Yup is imported.
 */
const makeLocalizedMessages = (value, callback, depth = 3, path = '') => {
  if (depth <= 0) return value
  const isFunc = typeof value === 'function'
  const isString = typeof value !== 'object'
  if (isFunc || isString) {
    return (...args) => {
      const fallback = isFunc ? () => invalidMessage : invalidMessage
      const func = () => {
        const locale = getLocale()
        if (locale === 'fr') return get(FORMS['fr'], path) || fallback
        if (locale === 'zh') return get(FORMS['zh'], path) || fallback
        return get(FORMS['en'], path) || fallback
      }
      return isFunc ? func()(...args) : func()
    }
  }
  for (const key in value) {
    value[key] = makeLocalizedMessages(
      value[key],
      callback,
      depth - 1,
      path ? `${path}.${key}` : key
    )
  }
  return value
}

// Makes a clone of the FORMS['en'] locale
// so we can iterate over it
const cloneLocale = (input, depth = 2, path = '') => {
  if (depth <= 0) return get(FORMS['en'], path) || ''
  const source = (path ? get(FORMS['en'], path) : FORMS['en']) || {}
  const keys = Object.keys(source)
  const value = input ? input : {}
  keys.forEach(key => {
    value[key] = cloneLocale(
      value[key],
      depth - 1,
      path ? `${path}.${key}` : key
    )
  })
  return value
}

const createLocale = () => makeLocalizedMessages(cloneLocale())

const value = createLocale()

Yup.setLocale(value)
